/**
 * `<GroupStep>` — the content pane for one benefit group: the "why" line, the
 * connector tiles, the list of already-connected sources (from the overview),
 * and the form dialogs each live tile opens.
 *
 * OAuth tiles reuse the managed-Airbyte initiate flow; it round-trips through
 * the provider and lands back on `/onboard` with `?connector=connected|error`
 * (handled by `OnboardRoute`).
 */

import { Spinner, toast } from "@cometchat-ai/ui-dashboard";
import { UserPlus } from "../../components/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusPill, type PillTone } from "../../components/status-pill";
import { useAdapters, useInitiateOAuth } from "../../tables/api/connectors";
import type { GroupConfig, TileConfig } from "../groups";
import type { OverviewGroup, PendingInvitation, SourceStatus } from "../api/onboarding";
import { DbCredsForm } from "./DbCredsForm";
import { InviteTeammateDialog } from "./InviteTeammateDialog";
import { KbSourceForm } from "./KbSourceForm";
import { SourceTile } from "./SourceTile";
import { SpecImportForm } from "./SpecImportForm";

const STATUS_TONE: Record<SourceStatus, PillTone> = {
  needed: "neutral",
  connecting: "info",
  connected: "success",
  action_needed: "warning",
  error: "danger",
};

type OpenForm = "kb" | "db" | "spec" | null;

/** A "technical" group has a hands-on step (DB creds / API spec) a non-technical
 *  founder may want to hand to an engineer — that's where the invite link lives. */
function isTechnicalGroup(group: GroupConfig): boolean {
  return group.tiles.some((tile) => tile.kind === "db" || tile.kind === "spec");
}

export function GroupStep({
  slug,
  group,
  overview,
  pendingInvitations,
}: {
  slug: string;
  group: GroupConfig;
  overview: OverviewGroup | undefined;
  pendingInvitations: PendingInvitation[];
}) {
  const { t } = useTranslation("onboarding");
  const adapters = useAdapters(slug);
  const initiateOAuth = useInitiateOAuth();
  const [openForm, setOpenForm] = useState<OpenForm>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  function openTile(tile: TileConfig) {
    if (tile.kind === "kb" || tile.kind === "db" || tile.kind === "spec") {
      setOpenForm(tile.kind);
      return;
    }
    if (tile.kind === "oauth" && tile.contractId) {
      const adapterId = adapters.data?.find((a) => a.id === tile.contractId)?.id;
      if (!adapterId) {
        toast.error(t("tile.comingSoon"));
        return;
      }
      // Return to this group after the provider round-trip.
      sessionStorage.setItem("tables.oauthReturn", `/onboard?group=${group.key}`);
      initiateOAuth.mutate(
        { adapterId, fields: {}, name: tile.label },
        { onError: () => toast.error(t("status.error")) },
      );
    }
  }

  const sources = overview?.sources ?? [];

  return (
    <div className="space-y-6">
      <p className="max-w-2xl text-sm text-muted-foreground" data-testid="onboarding-group-why">
        {t(`groups.${group.key}.why`)}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {group.tiles.map((tile) => (
          <SourceTile key={tile.id} tile={tile} onOpen={openTile} />
        ))}
      </div>

      {isTechnicalGroup(group) ? (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            data-testid="onboarding-invite-link"
            className="inline-flex w-fit items-center gap-1.5 rounded-md text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            {t("invite.link")}
          </button>

          {pendingInvitations.length > 0 ? (
            <ul
              className="flex flex-wrap gap-1.5"
              data-testid="onboarding-pending-invites"
              aria-label={t("invite.waitingLabel")}
            >
              {pendingInvitations.map((inv) => (
                <li key={inv.email}>
                  <StatusPill tone="info">{t("invite.waiting", { email: inv.email })}</StatusPill>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <section className="space-y-2">
        <h3 className="exp-heading-font text-sm tracking-tight text-foreground">
          {t("connectedList.title")}
        </h3>
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("connectedList.empty")}</p>
        ) : (
          <ul
            className="divide-y divide-border rounded-lg border border-border"
            data-testid="onboarding-connected-list"
          >
            {sources.map((s) => (
              <li
                key={`${s.kind}-${s.id}`}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{s.name}</p>
                  {s.detail ? (
                    <p className="truncate text-xs text-muted-foreground">{s.detail}</p>
                  ) : null}
                </div>
                <span className="flex items-center gap-1.5">
                  {s.status === "connecting" ? (
                    <Spinner className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : null}
                  <StatusPill tone={STATUS_TONE[s.status]}>{t(`status.${s.status}`)}</StatusPill>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <KbSourceForm
        slug={slug}
        open={openForm === "kb"}
        onOpenChange={(o) => setOpenForm(o ? "kb" : null)}
      />
      <DbCredsForm
        slug={slug}
        open={openForm === "db"}
        onOpenChange={(o) => setOpenForm(o ? "db" : null)}
      />
      <SpecImportForm
        slug={slug}
        open={openForm === "spec"}
        onOpenChange={(o) => setOpenForm(o ? "spec" : null)}
      />
      <InviteTeammateDialog slug={slug} open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
